import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  Copyable,
  CurrencyAmountUI,
  cn,
  DateUI,
  entryFromEventFragment,
  entryFromFragment,
  Icon,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusUI,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TimeLogPersonActor,
  Txt,
  toast,
  uppercaseStatusToStatus,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import type React from "react";
import { Suspense } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiRobot2Line } from "react-icons/ri";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { AdminCurrencycloudPaymentSection } from "~/components/Currencycloud/AdminCurrencycloudPaymentSection";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AdminEburyPaymentSection } from "~/components/Ebury/AdminEburyPaymentSection";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { PaneActions } from "~/components/operations/actions";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { TransferGroupUI } from "~/components/TransferGroup/TransferGroupUI";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

type TimeLogEntry = ReturnType<typeof entryFromFragment>;

function TimeLogSystemActor({
  actor,
  prefix,
}: {
  actor: { kind: "SYSTEM"; id: string; name: string };
  prefix?: React.ReactNode;
}) {
  return (
    <>
      {prefix ? <span className="whitespace-nowrap">{prefix}</span> : null}
      <span className="-my-1 inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-white p-1 pr-2 align-middle text-xs shadow-sm">
        <div className="flex items-center justify-center pl-1">
          <RiRobot2Line />
        </div>
        <div className="max-w-[30ch] truncate">
          {actor.name} {actor.id}
        </div>
      </span>
    </>
  );
}

const TimeLogEntryCompact = ({ at, description, by }: TimeLogEntry) => {
  return (
    <div className="inline-grid place-content-start gap-1">
      <Txt color="secondary" size="xs" className="whitespace-nowrap">
        {DateTime.fromJSDate(at).toLocaleString(DateTime.DATETIME_MED)}
      </Txt>
      <Txt size="sm">
        {description}
        &nbsp;
        {by && by.kind === "PERSON" ? (
          <TimeLogPersonActor prefix="by " actor={by} />
        ) : null}
        {by && by.kind === "SYSTEM" ? (
          <TimeLogSystemActor prefix="by " actor={by} />
        ) : null}
      </Txt>
    </div>
  );
};

const TimeLogCompact = ({
  entries,
  className,
}: {
  entries: TimeLogEntry[];
  className?: string;
}) => {
  const stortedEntriesDateDesc = entries.sort(
    (a, z) => z.at.getTime() - a.at.getTime(),
  );

  return (
    <div className={cn(className, "grid place-content-start gap-4")}>
      {stortedEntriesDateDesc.map((entry, i) => (
        <TimeLogEntryCompact key={entry.at.toISOString() + i} {...entry} />
      ))}
    </div>
  );
};

export const Route = createFileRoute("/__auth/payouts_/$payoutId")({
  head: () => opsHead("Payout"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { payoutId } = Route.useParams();

  const { data, error } = useSuspenseQuery(
    graphql(
      `
        query AdminPayoutPage($payoutId: ID!) {
          payout: node(id: $payoutId) {
            id
            ... on Payout {
              shortRef
              status
              canUpdateSourceAsset
              ledgerTransactions {
                id
                ...RelatedLedgerTransactions
              }
              requestedForDate
              amount {
                amount
                currency
              }
              approvalRequest {
                id
                approvalPolicy {
                  id
                  numUserReviewsRequired
                }
                currentApprovedCount
                result
              }
              paymentReference
              sourceAsset {
                id
                class
                ... on FXAsset {
                  id
                  provider {
                    internalName
                  }
                }
              }
              beneficiaryAccount {
                id
                ...BeneficiaryAssetCardFields
                ... @defer {
                  eburyBeneficiary {
                    id
                    ...AdminEburyBeneficiaryCard
                  }
                }
                ... @defer {
                  currencycloudBeneficiary {
                    id
                    ...AdminCurrencycloudBeneficiaryCard
                  }
                }
              }
              businessEntity {
                id
                name
                organisation {
                  id
                  name
                  squareLogoUrl
                }
                assets {
                  ...AssetCardFields
                }
              }
              executionMethod
              expectedProcessingDate
              updates {
                kind
                ...TimeLogEntry
              }
              events {
                ...TimeLogEventEntry
              }
              ...OperationsPayoutActions
              eburyPayment {
                id
                ...AdminEburyPaymentSection
              }
              currencycloudPayment {
                id
                ...AdminCurrencycloudPaymentSection
              }
              transferGroup {
                  id
                  kind
                  ...AdminTransferGroup
              }
            }
          }
        }
      `,
    ),
    {
      variables: {
        payoutId,
      },
    },
  );

  const payout = data?.payout;
  if (!payout || payout.__typename !== "Payout") {
    throw new NotFoundError();
  }

  useOpsTitle(`Payout #${payout.shortRef}`);

  const { updates, executionMethod, approvalRequest } = payout;

  const beneficiaryAccount = payout.beneficiaryAccount;
  if (!beneficiaryAccount) {
    throw new Error("expected beneficiary account");
  }

  const businessEntity = payout.businessEntity;
  if (!businessEntity) {
    throw new Error("expected business entity on payout");
  }

  const sourceAsset = payout.sourceAsset;

  const sourceAssetOptions = (payout.businessEntity?.assets ?? []).filter(
    (asset) => asset.currency === payout.amount.currency,
  );

  const creationEvent = updates?.find(
    (update) => update.kind === "created" && update.by,
  );

  const [updateSourceAsset] = useMutation(
    graphql(`
      mutation UpdateSourceAsset($payoutId: ID!, $sourceAssetId: ID!) {
          updatePayout(input: {
             id: $payoutId,
             sourceAssetId: $sourceAssetId
          }) {
              payout {
                  id
                  sourceAsset {
                      id
                  }
                  executionMethod
                  ...OperationsPayoutActions
                  events {
                    ...TimeLogEventEntry
                  }
              }
          }
      }
  `),
    {
      onError() {
        toast({
          title: "Something went wrong",
          description: "Please refresh the page and try again",
          kind: "error",
        });
      },
    },
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Payout #${payout.shortRef}`}
        subtitle={<div className="font-mono">{payout.id}</div>}
      />

      <Tabs defaultValue="details">
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div>
            {payout.status && (
              <DetailRow
                label="Status"
                value={
                  <StatusUI status={uppercaseStatusToStatus[payout.status]} />
                }
              />
            )}
            {creationEvent ? (
              <DetailRow
                label="Created by"
                value={
                  <TimeLogPersonActor
                    actor={{
                      ...creationEvent.by,
                      kind: "PERSON",
                      avatarUrl: creationEvent?.by?.avatarUrl ?? undefined,
                    }}
                  />
                }
              />
            ) : null}
            <DetailRow
              label="Client"
              value={
                <div className="flex flex-row items-center gap-2">
                  <Icon
                    size="8"
                    imageUrl={
                      businessEntity.organisation?.squareLogoUrl ?? undefined
                    }
                    fallbackIcon={<PiBuildingOffice />}
                  />
                  <div className="flex flex-col">
                    <Link
                      to={`/organisations/${businessEntity.organisation?.id}/business-entities/${businessEntity.id}`}
                    >
                      <Txt
                        weight="semibold"
                        size="sm"
                        as="div"
                        className="max-w-30 truncate hover:underline"
                      >
                        {businessEntity.name}
                      </Txt>
                    </Link>
                    <Link
                      to={`/organisations/${businessEntity.organisation?.id}`}
                    >
                      <Txt
                        color="secondary"
                        size="sm"
                        as="div"
                        className="max-w-30 truncate hover:underline"
                      >
                        {businessEntity.organisation?.name}
                      </Txt>
                    </Link>
                  </div>
                </div>
              }
            />
            <DetailRow
              label="Amount"
              value={
                <CurrencyAmountUI
                  {...payout.amount}
                  size="lg"
                  showSuperscript={false}
                />
              }
            />
            <DetailRow
              label="Date"
              value={
                <DateUI date={payout.requestedForDate} variant="DATETIME_TZ" />
              }
            />
            <DetailRow
              label="Expected processing date"
              value={
                payout.expectedProcessingDate ? (
                  <DateUI
                    date={payout.expectedProcessingDate}
                    variant="DATE_TZ"
                  />
                ) : (
                  "-"
                )
              }
            />
            <DetailRow
              label="Source account"
              value={
                <Select
                  disabled={!payout.canUpdateSourceAsset}
                  name="sourceAssetId"
                  defaultValue={sourceAsset?.id}
                  onValueChange={(v) => {
                    const newSourceAsset = sourceAssetOptions.find(
                      (o) => o.id === v,
                    );
                    if (!newSourceAsset) {
                      throw new Error("expected source asset");
                    }
                    updateSourceAsset({
                      variables: {
                        payoutId,
                        sourceAssetId: newSourceAsset.id,
                      },
                      optimisticResponse: {
                        __typename: "Mutation",
                        updatePayout: {
                          __typename: "UpdatePayoutPayload",
                          payout: {
                            __typename: "Payout",
                            id: payoutId,
                            sourceAsset: {
                              __typename: newSourceAsset.__typename,
                              id: newSourceAsset.id,
                            },
                            executionMethod: "MANUAL",
                          },
                        },
                      },
                    });
                  }}
                >
                  <SelectTrigger className="h-auto w-fit min-w-[30ch] text-start">
                    <div className="flex items-center gap-4 pe-4">
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {[...sourceAssetOptions]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((a) => (
                        <SelectItem value={a.id} key={a.id}>
                          <AssetCard asset={a} />
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              }
            />
            <DetailRow
              label="To Account"
              value={
                <div className="flex items-center gap-4">
                  <Link to={`/beneficiary-accounts/${beneficiaryAccount.id}`}>
                    <div className="flex w-fit cursor-pointer flex-col gap-4 rounded-sm p-2 hover:bg-zinc-100">
                      <AssetCard
                        beneficiaryAccount={beneficiaryAccount}
                        variant="IDENTIFIER"
                      />
                    </div>
                  </Link>

                  {executionMethod === "EBURY_PAYMENT" && (
                    <AdminEburyBeneficiaryCard
                      eburyBeneficiary={beneficiaryAccount.eburyBeneficiary}
                      beneficiaryAccountId={beneficiaryAccount.id}
                      errors={error?.graphQLErrors?.filter((gqlError) => {
                        return (
                          gqlError.path?.at(0) === "payout" &&
                          gqlError.path?.at(1) === "beneficiaryAccount" &&
                          gqlError.path?.at(2) === "eburyBeneficiary"
                        );
                      })}
                    />
                  )}

                  {executionMethod === "CURRENCYCLOUD_PAYMENT" && (
                    <AdminCurrencycloudBeneficiaryCard
                      currencycloudBeneficiary={
                        beneficiaryAccount.currencycloudBeneficiary
                      }
                      beneficiaryAccountId={beneficiaryAccount.id}
                      errors={error?.graphQLErrors?.filter((gqlError) => {
                        return (
                          gqlError.path?.at(0) === "payout" &&
                          gqlError.path?.at(1) === "beneficiaryAccount" &&
                          gqlError.path?.at(2) === "currencycloudBeneficiary"
                        );
                      })}
                    />
                  )}
                </div>
              }
            />
            <DetailRow
              label="Payment reference"
              value={
                <div className="flex items-center gap-6">
                  <Copyable
                    tooltipSide="right"
                    className="bg-neutral-100 font-mono text-neutral-700"
                  >
                    {payout.paymentReference}
                  </Copyable>

                  <span className="text-slate-500 text-xs italic">
                    Include in payment instructions for automatic
                    reconcilliation
                  </span>
                </div>
              }
            />

            <AdminEburyPaymentSection
              enableDownloadReceiptButton={payout.status === "COMPLETED"}
              businessEntityId={payout.businessEntity?.id ?? ""}
              eburyPayment={payout.eburyPayment}
              errors={error?.graphQLErrors?.filter((gqlError) => {
                return (
                  gqlError.path?.at(0) === "payout" &&
                  gqlError.path?.at(1) === "eburyPayment"
                );
              })}
            />

            <AdminCurrencycloudPaymentSection
              currencycloudPayment={payout.currencycloudPayment}
              errors={error?.graphQLErrors?.filter((gqlError) => {
                return (
                  gqlError.path?.at(0) === "payout" &&
                  gqlError.path?.at(1) === "currencycloudPayment"
                );
              })}
            />

            {approvalRequest && (
              <DetailRow
                label="Approval"
                value={
                  <>
                    <DetailSubRow
                      label={"Status"}
                      value={
                        <StatusUI
                          status={
                            uppercaseStatusToStatus[approvalRequest.result]
                          }
                        />
                      }
                    />
                    <DetailSubRow
                      label={"# required"}
                      value={
                        approvalRequest.approvalPolicy
                          ?.numUserReviewsRequired ?? "Unknown"
                      }
                    />
                    <DetailSubRow
                      label={"# approved"}
                      value={approvalRequest.currentApprovedCount}
                    />
                  </>
                }
              />
            )}

            {updates ? (
              <DetailRow
                label="Updates"
                value={
                  <TimeLogCompact entries={updates.map(entryFromFragment)} />
                }
              />
            ) : null}

            {payout.events ? (
              <DetailRow
                label="Event log"
                value={
                  <TimeLogCompact
                    entries={payout.events.map(entryFromEventFragment)}
                  />
                }
              />
            ) : null}

            <div className="mt-4">
              <PaneActions {...payout} />
            </div>

            {payout.transferGroup && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                  <h2 className="mt-16 border-b font-semibold text-lg">
                    Transfer group ({prettyPrintEnum(payout.transferGroup.kind)}
                    )
                  </h2>

                  <TransferGroupUI
                    transferGroup={payout.transferGroup}
                    currentFundMovementId={null}
                  />
                </div>

                <Link to={`/transfer-groups/${payout.transferGroup.id}`}>
                  <Button variant="secondary">View transfer group</Button>
                </Link>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={payout.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
